export {};
import { trackEvent } from '../utils/analytics';

// Minimal YouTube IFrame API types
interface YTPlayer {
  isMuted(): boolean;
  playVideo(): void;
  pauseVideo(): void;
  mute(): void;
  unMute(): void;
  getPlayerState(): number;
}

interface YTPlayerOptions {
  videoId: string;
  playerVars?: Record<string, number | string>;
  events?: {
    onReady?: (event: { target: YTPlayer }) => void;
    onStateChange?: (event: { data: number }) => void;
  };
}

interface YTNamespace {
  Player: new (elementId: string, options: YTPlayerOptions) => YTPlayer;
  PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
}

interface SwiperInstance {
  slides: HTMLElement[];
  activeIndex: number;
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
    onHpVideoSlideChange?: (swiper: SwiperInstance) => void;
  }
}

// Track all active controllers for cross-video pause
const activeControllers: VideoController[] = [];
let isSectionVisible = false;
// Set in theme.liquid; null outside product pages
const isProductPage = !!(window as unknown as { __productIdFromTemplate?: number | null }).__productIdFromTemplate;
let previousSlideIndex = 0;
let maxSlideIndex = 0;
// Shared mute state across all videos in this section, so switching slides keeps the user's choice
let isMuted = true;

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve();
      return;
    }
    window.onYouTubeIframeAPIReady = resolve;
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });
}

async function initVideoSlider(): Promise<void> {
  const blocks = document.querySelectorAll<HTMLElement>('.wrapper-item-video');
  if (!blocks.length) return;

  // Clear previous controllers (e.g. theme editor re-render)
  activeControllers.length = 0;

  const hasYouTube = [...blocks].some((b) => b.dataset.videoType === 'youtube');
  if (hasYouTube) await loadYouTubeAPI();

  blocks.forEach((block) => {
    activeControllers.push(new VideoController(block));
  });

  // Product pages: never autoplay. The Shopify video only downloads on click (preload="none"),
  // otherwise a 1080p file competes with the LCP image on mobile.
  if (isProductPage) return;

  // Mobile: autoplay first video immediately
  if (window.innerWidth < 750 && activeControllers.length > 0) {
    activeControllers[0].play();
    return;
  }

  // Desktop: autoplay when section scrolls into view
  const sectionEl = blocks[0].closest('.section-see-it-in-real-life');
  if (!sectionEl) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isSectionVisible = entry.isIntersecting;
        if (entry.isIntersecting) {
          const first = activeControllers.find((c) => !c.isPlaying());
          if (first) first.play();
        } else {
          activeControllers.forEach((ctrl) => {
            if (ctrl.isPlaying()) ctrl.pause();
          });
        }
      });
    },
    { threshold: 0.25 }
  );
  observer.observe(sectionEl);
}

// Called from the Swiper init in the Liquid template
function onHpVideoSlideChange(swiper: SwiperInstance): void {
  const fromIndex = previousSlideIndex;
  const toIndex = swiper.activeIndex;
  const activeSlide = swiper.slides[toIndex];
  if (!activeSlide) return;

  const activeVideo = activeSlide.querySelector<HTMLElement>('.wrapper-item-video');
  const videoName = activeVideo?.dataset.videoName ?? '';

  if (toIndex !== fromIndex) {
    trackEvent('video_slider_swipe', { from_index: fromIndex, to_index: toIndex, video_name: videoName });
  }
  if (toIndex > maxSlideIndex) {
    maxSlideIndex = toIndex;
    trackEvent('video_slider_depth', { depth_index: toIndex, video_name: videoName, total_slides: swiper.slides.length });
  }
  previousSlideIndex = toIndex;

  const shouldAutoplay = !isProductPage && (window.innerWidth < 750 || isSectionVisible);
  if (!shouldAutoplay) return;

  activeControllers.forEach((ctrl) => {
    if (ctrl.block === activeVideo) {
      if (ctrl.isReady && !ctrl.isPlaying()) ctrl.play();
    } else {
      if (ctrl.isPlaying()) ctrl.pause();
    }
  });
}

class VideoController {
  block: HTMLElement;
  type: string;
  playBtn: HTMLElement | null;
  volumeBtn: HTMLElement | null;
  placeholder: HTMLElement | null;
  hasStarted: boolean;
  isReady: boolean;
  pendingAutoplay: boolean;
  videoId: string | undefined;
  iframeId: string;
  ytPlayer: YTPlayer | null;
  videoEl: HTMLVideoElement | null;
  loader: HTMLElement | null;

  constructor(block: HTMLElement) {
    this.block = block;
    this.type = block.dataset.videoType ?? '';
    this.playBtn = block.querySelector<HTMLElement>('.wrapper-item-video-play-btn');
    this.volumeBtn = block.querySelector<HTMLElement>('.wrapper-item-video-volume-btn');
    this.placeholder = block.querySelector<HTMLElement>('.wrapper-item-placeholder');
    this.hasStarted = false;
    this.isReady = false;
    this.pendingAutoplay = false;
    this.videoId = block.dataset.youtubeId;
    this.iframeId = `yt-player-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    this.ytPlayer = null;
    this.videoEl = block.querySelector<HTMLVideoElement>('.wrapper-item-native-video');
    this.loader = block.querySelector<HTMLElement>('.wrapper-item-video-loader');

    if (!this.playBtn || !this.volumeBtn) return;
    this.init();
  }

  init(): void {
    if (this.type === 'shopify') {
      this.isReady = true;
      this.volumeBtn!.classList.add('muted');
      this.videoEl?.addEventListener('playing', () => {
        this.loader?.classList.remove('is-visible');
        this.setPlaying(true);
      });
      this.videoEl?.addEventListener('waiting', () => {
        this.loader?.classList.add('is-visible');
      });
    } else if (this.type === 'youtube') {
      this.createYTPlayer();
    }
    this.bindEvents();
  }

  bindEvents(): void {
    this.playBtn!.addEventListener('click', (e) => { e.stopPropagation(); this.togglePlay(); });
    this.volumeBtn!.addEventListener('click', (e) => { e.stopPropagation(); this.toggleVolume(); });
    this.block.addEventListener('click', () => this.togglePlay());

    const shopToggle = this.block.querySelector<HTMLElement>('.wrapper-item-video-shop-toggle');
    if (shopToggle) {
      shopToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const products = this.block.querySelector<HTMLElement>('.wrapper-item-video-shop-products');
        const expanded = shopToggle.getAttribute('aria-expanded') === 'true';
        shopToggle.setAttribute('aria-expanded', String(!expanded));
        products?.classList.toggle('active');
      });

      this.block.querySelector('.wrapper-item-video-shop-products')
        ?.addEventListener('click', (e) => e.stopPropagation());
    }
  }

  createYTPlayer(): void {
    if (this.ytPlayer || !window.YT || !this.videoId) return;

    const playerDiv = document.createElement('div');
    playerDiv.id = this.iframeId;
    this.block.appendChild(playerDiv);

    this.ytPlayer = new window.YT.Player(this.iframeId, {
      videoId: this.videoId,
      playerVars: {
        autoplay: 0, mute: 1, controls: 0, modestbranding: 1,
        rel: 0, showinfo: 0, iv_load_policy: 3, playsinline: 1,
        disablekb: 1, fs: 0, loop: 1, playlist: this.videoId,
      },
      events: {
        onReady: this.onYTReady.bind(this),
        onStateChange: this.onYTStateChange.bind(this),
      },
    });
  }

  onYTReady(): void {
    this.isReady = true;
    this.applyMuteState(isMuted);
    if (this.pendingAutoplay) {
      this.pendingAutoplay = false;
      this.play();
    }
  }

  onYTStateChange(event: { data: number }): void {
    if (event.data === 3) this.loader?.classList.add('is-visible'); // buffering
    this.setPlaying(!!window.YT && event.data === window.YT.PlayerState.PLAYING);
  }

  pauseOthers(): void {
    activeControllers.forEach((ctrl) => {
      if (ctrl !== this && ctrl.isPlaying()) ctrl.pause();
    });
  }

  isPlaying(): boolean {
    if (this.type === 'shopify') return !!this.videoEl && !this.videoEl.paused;
    if (this.type === 'youtube' && this.ytPlayer && this.isReady && window.YT) {
      return this.ytPlayer.getPlayerState() === window.YT.PlayerState.PLAYING;
    }
    return false;
  }

  play(trigger: 'user' | 'autoplay' = 'autoplay'): void {
    if (!this.isReady) { this.pendingAutoplay = true; return; }
    this.pauseOthers();
    const videoIndex = activeControllers.indexOf(this);
    const videoName = this.block.dataset.videoName ?? '';
    if (videoIndex >= 0) {
      trackEvent('video_play', { section: 'hp_video_slider', video_index: videoIndex, video_name: videoName, trigger });
    }
    if (this.type === 'shopify' && this.videoEl) {
      this.loader?.classList.add('is-visible');
      this.videoEl.play().catch((err: Error) => {
        if (err.name === 'AbortError') {
          this.loader?.classList.remove('is-visible');
        } else {
          throw err;
        }
      });
      // setPlaying(true) deferred to the 'playing' event in init()
    } else if (this.type === 'youtube' && this.ytPlayer) {
      this.loader?.classList.add('is-visible');
      this.ytPlayer.playVideo();
    }
  }

  pause(trigger: 'user' | 'autoplay' = 'autoplay'): void {
    this.pendingAutoplay = false;
    this.loader?.classList.remove('is-visible');
    const videoIndex = activeControllers.indexOf(this);
    const videoName = this.block.dataset.videoName ?? '';
    if (videoIndex >= 0) {
      trackEvent('video_pause', { section: 'hp_video_slider', video_index: videoIndex, video_name: videoName, trigger });
    }
    if (this.type === 'shopify' && this.videoEl) {
      this.videoEl.pause();
      this.setPlaying(false);
    } else if (this.type === 'youtube' && this.ytPlayer && this.isReady) {
      this.ytPlayer.pauseVideo();
    }
  }

  togglePlay(): void {
    if (!this.isReady) return;
    this.isPlaying() ? this.pause('user') : this.play('user');
  }

  toggleVolume(): void {
    isMuted = !isMuted;
    activeControllers.forEach((ctrl) => ctrl.applyMuteState(isMuted));
  }

  applyMuteState(muted: boolean): void {
    if (this.type === 'shopify' && this.videoEl) {
      this.videoEl.muted = muted;
      this.volumeBtn?.classList.toggle('muted', muted);
    } else if (this.type === 'youtube' && this.ytPlayer && this.isReady) {
      if (muted) this.ytPlayer.mute(); else this.ytPlayer.unMute();
      this.volumeBtn?.classList.toggle('muted', muted);
    }
  }

  setPlaying(playing: boolean): void {
    if (playing) {
      this.block.classList.add('playing', 'started');
      this.loader?.classList.remove('is-visible');
      this.hasStarted = true;
      if (this.placeholder) this.placeholder.style.display = 'none';
    } else {
      this.block.classList.remove('playing');
      this.loader?.classList.remove('is-visible');
    }
  }
}

window.onHpVideoSlideChange = onHpVideoSlideChange;
document.addEventListener('DOMContentLoaded', initVideoSlider);
